import { useRef, useState } from 'react'
import { Download, FileUp, FolderOpen, Loader2, Merge, X } from 'lucide-react'

import { useAppStore } from '@core/store'
import { downloadBlob } from '@core/lib/downloadBlob'
import { viewerApi } from '@viewer/lib/viewerApi'
import { AVISO, BTN, BTN_PRIMARIO, NOTA, OK, ROTULO } from '@viewer/dados/estilos'
import { juntarIfc, OPCOES_POR_OMISSAO, type OpcoesDeFusao, type ResultadoDaFusao } from './lib/juntarIfc'

function tamanho(b: number): string {
  return b >= 1048576 ? `${(b / 1048576).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(b / 1024))} kB`
}

/** Juntar IFC: ferramenta à parte.
 *
 *  Não mexe no modelo aberto: trabalha sobre ficheiros escolhidos do
 *  disco, que podem nem estar carregados, e devolve um ficheiro novo.
 *  Por isso não é um painel do modelo; está no friso por conveniência. */
export default function JuntarIfcPanel({ aoFechar }: { aoFechar: () => void }) {
  const principalRef = useRef<HTMLInputElement>(null)
  const outrosRef = useRef<HTMLInputElement>(null)
  const [principal, setPrincipal] = useState<File | null>(null)
  const [outros, setOutros] = useState<File[]>([])
  const [opcoes, setOpcoes] = useState<OpcoesDeFusao>(OPCOES_POR_OMISSAO)
  const [aJuntar, setAJuntar] = useState(false)
  const [registo, setRegisto] = useState<{ l: string; aviso: boolean }[]>([])
  const [resultado, setResultado] = useState<ResultadoDaFusao | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const juntar = async () => {
    if (!principal || outros.length === 0) return
    setAJuntar(true)
    setErro(null)
    setResultado(null)
    setRegisto([])
    const t0 = performance.now()
    try {
      const r = await juntarIfc(principal, outros, opcoes, (l, aviso) => setRegisto((x) => [...x, { l, aviso: Boolean(aviso) }]))
      setResultado(r)
      setRegisto((x) => [...x, { l: `Feito em ${((performance.now() - t0) / 1000).toFixed(1).replace('.', ',')} s.`, aviso: false }])
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    } finally {
      setAJuntar(false)
    }
  }

  const abrir = async (substituir: boolean) => {
    if (!resultado) return
    const f = new File([resultado.blob], resultado.nome)
    const aberto = await viewerApi.loadIfcFile(f, { replace: substituir })
    if (!aberto.ok) {
      setErro(`Não foi possível abrir ${resultado.nome} no viewer: ${aberto.erro}`)
      return
    }
    useAppStore.getState().pushToast(`${resultado.nome} aberto no viewer.`, { type: 'success' })
  }

  const radio = <K extends keyof OpcoesDeFusao>(chave: K, valor: OpcoesDeFusao[K], rotulo: string) => (
    <label className="flex items-center gap-1">
      <input type="radio" checked={opcoes[chave] === valor} onChange={() => setOpcoes({ ...opcoes, [chave]: valor })} />
      {rotulo}
    </label>
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-1 border-b border-[var(--color-border)] px-2 py-1">
        <Merge size={13} className="text-[var(--color-accent)]" />
        <span className="text-xs font-medium text-[var(--color-text)]">Juntar IFC</span>
        <button type="button" onClick={aoFechar} className="ml-auto rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]" title="Fechar">
          <X size={14} />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-3 text-[11px]">
        <p className={NOTA}>
          Junta vários IFC num ficheiro só, sem os abrir no viewer: lê o texto dos ficheiros, não a geometria, por isso
          aguenta ficheiros grandes. O primeiro é o principal: os outros entram no projecto, sítio, edifício e pisos
          dele. Se um ficheiro estiver noutra unidade de comprimento, é convertido pelas unidades que os dois
          declaram.
        </p>
        <input ref={principalRef} type="file" accept=".ifc" className="hidden" onChange={(e) => { setPrincipal(e.target.files?.[0] ?? null); e.target.value = '' }} />
        <input
          ref={outrosRef}
          type="file"
          accept=".ifc"
          multiple
          className="hidden"
          onChange={(e) => {
            const fs = [...(e.target.files ?? [])]
            e.target.value = ''
            setOutros((x) => [...x, ...fs])
          }}
        />
        <div className="flex flex-col gap-1">
          <span className={ROTULO}>Principal</span>
          <div className="flex items-center gap-2">
            <button type="button" className={BTN} onClick={() => principalRef.current?.click()} disabled={aJuntar}>
              <FileUp size={12} /> {principal ? 'Trocar' : 'Escolher'}
            </button>
            {principal && <span className="truncate">{principal.name} <span className="text-[var(--color-text-dim)]">({tamanho(principal.size)})</span></span>}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className={ROTULO}>A juntar</span>
          <div className="flex items-center gap-2">
            <button type="button" className={BTN} onClick={() => outrosRef.current?.click()} disabled={aJuntar}>
              <FileUp size={12} /> Acrescentar
            </button>
            {outros.length > 0 && (
              <button type="button" className={BTN} onClick={() => setOutros([])} disabled={aJuntar}>Limpar</button>
            )}
          </div>
          <ul className="flex flex-col gap-0.5">
            {outros.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-1">
                <span className="truncate">{f.name}</span>
                <span className="text-[var(--color-text-dim)]">({tamanho(f.size)})</span>
                <button type="button" className="ml-auto text-[var(--color-text-dim)] hover:text-[var(--color-error-text)]" onClick={() => setOutros(outros.filter((_, j) => j !== i))} disabled={aJuntar}>
                  <X size={11} />
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 rounded border border-[var(--color-border)] p-2">
          <span className={ROTULO}>Sítios (IfcSite)</span>
          <span className="flex gap-2">{radio('sitios', 'merge', 'juntar num')}{radio('sitios', 'keep', 'manter separados')}</span>
          <span className={ROTULO}>Edifícios</span>
          <span className="flex gap-2">{radio('edificios', 'merge', 'juntar num')}{radio('edificios', 'keep', 'manter separados')}</span>
          <span className={ROTULO}>Pisos</span>
          <span className="flex flex-wrap gap-2">
            {radio('pisos', 'name', 'pelo nome')}
            {radio('pisos', 'elevation', 'pela cota')}
            {radio('pisos', 'keep', 'separados')}
          </span>
          <span className={ROTULO}>IfcOwnerHistory</span>
          <span className="flex gap-2">{radio('historico', 'keep', 'de cada ficheiro')}{radio('historico', 'merge', 'o do principal')}</span>
          <span className={ROTULO}>Repetições</span>
          <label className="flex items-center gap-1" title="Pontos, direcções e estilos iguais passam a ser um só. Não perde informação e não altera GlobalIds.">
            <input type="checkbox" checked={opcoes.reduzirRepeticoes} onChange={(e) => setOpcoes({ ...opcoes, reduzirRepeticoes: e.target.checked })} />
            juntar recursos iguais (ficheiro menor)
          </label>
          <span className={ROTULO}>GlobalIds</span>
          <label
            className="flex items-center gap-1"
            title="Quando o mesmo GlobalId aparece em mais de um ficheiro (por exemplo, duas versões do mesmo modelo), atribui um GlobalId novo às repetições. Sem esta opção o ficheiro junto fica com GlobalIds repetidos, o que não é IFC válido. Com ela, as entidades reparadas deixam de corresponder ao GlobalId original (tópicos BCF, comparações)."
          >
            <input type="checkbox" checked={opcoes.repararGlobalIds} onChange={(e) => setOpcoes({ ...opcoes, repararGlobalIds: e.target.checked })} />
            reparar GlobalIds repetidos
          </label>
        </div>
        {opcoes.edificios === 'keep' && opcoes.pisos !== 'keep' && (
          <div className={AVISO}>Com edifícios separados, os pisos também ficam separados.</div>
        )}
        <div className="flex gap-1">
          <button type="button" className={BTN_PRIMARIO} onClick={() => void juntar()} disabled={!principal || outros.length === 0 || aJuntar}>
            {aJuntar ? <Loader2 size={12} className="animate-spin" /> : <Merge size={12} />} {aJuntar ? 'A juntar…' : 'Juntar'}
          </button>
        </div>
        {erro && <div className={AVISO}>{erro}</div>}
        {resultado && (
          <div className="flex flex-col gap-1">
            <div className={resultado.falhados.length === 0 ? OK : AVISO}>
              {resultado.nome}: {tamanho(resultado.bytes)}
              {resultado.falhados.length > 0 && `. Não entraram: ${resultado.falhados.map((f) => f.nome).join(', ')}.`}
            </div>
            <div className="flex flex-wrap gap-1">
              <button type="button" className={BTN_PRIMARIO} onClick={() => downloadBlob(resultado.blob, resultado.nome)}>
                <Download size={12} /> Descarregar
              </button>
              <button type="button" className={BTN} onClick={() => void abrir(true)}>
                <FolderOpen size={12} /> Abrir no viewer
              </button>
              <button type="button" className={BTN} onClick={() => void abrir(false)}>
                Juntar à federação
              </button>
            </div>
            {resultado.globalIdsRepetidos > 0 && (
              <div className={AVISO}>
                {resultado.globalIdsReparados
                  ? `${resultado.globalIdsRepetidos} entidade(s) tinham um GlobalId já usado noutro ficheiro e receberam um GlobalId novo. Essas entidades deixam de corresponder ao GlobalId original (tópicos BCF, comparações).`
                  : `O ficheiro junto tem ${resultado.globalIdsRepetidos} GlobalId(s) repetido(s), o que não é IFC válido. Para os corrigir, marcar "reparar GlobalIds repetidos" e juntar novamente.`}
              </div>
            )}
            {resultado.avisos.length > 0 && (
              <div className={AVISO}>Houve {resultado.avisos.length} aviso(s): ver o registo abaixo.</div>
            )}
          </div>
        )}
        {registo.length > 0 && (
          <details className="text-[10px]" open={aJuntar}>
            <summary className="cursor-pointer text-[var(--color-text-muted)]">Registo (as mensagens do motor vêm em inglês)</summary>
            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-[var(--color-bg)] p-1 font-mono text-[10px]">
              {registo.map((r, i) => (
                <div key={i} className={r.aviso ? 'text-[var(--color-warn-text)]' : ''}>{r.l}</div>
              ))}
            </pre>
          </details>
        )}
        <p className={`${NOTA} mt-auto`}>
          Motor de fusão: <a className="underline" href="https://github.com/ltorrecilla/liteaeco" target="_blank" rel="noreferrer">liteAECO</a>{' '}
          (Luis Torrecilla, Apache-2.0), incluído sem alterações e a correr no teu browser. Os ficheiros não saem da tua
          máquina.
        </p>
      </div>
    </div>
  )
}

